// Haas supplier → SVHC conformity template.
//
// Mirrors backend/templates/ingestion/haas_supplier_svhc.py.  When the user
// imports a bucket via the Lieferanten Deklarationen AP, the executor:
//
//   1. updates the anchored supplier's name to "AURUBIS AG"
//   2. traverses supplier → component and filters to article LT10002
//      (creates the component on miss)
//   3. pk-matches an SVHC substance by CAS 7439-92-1 (creates on miss),
//      and sets its substance_name to "Blei"
//   4. writes "> 0,1 %" into that component's svhc_concentration
//   5. links the supplier to the component
//   6. links the component to the SVHC substance
//
// All literals live here.  Swapping suppliers / CAS / concentration means
// authoring a sibling module — the executor and diff view never need to
// change.
//
// Exported as a plain object; registry.ts imports and registers it.  We
// deliberately don't self-register via a side-effect import because ESM
// hoists imports above `const` initialisers, which puts the registry's
// Map in the temporal dead zone when a template module loads.

import type { IngestionTemplate } from "../types"

const COMPONENT_ARTICLE_NR = "LT10002"
const SVHC_CAS_NUMBER = "7439-92-1"
const SVHC_SUBSTANCE_NAME = "Blei"
const SVHC_CONCENTRATION = "> 0,1 %"
const SUPPLIER_NAME = "AURUBIS AG"

export const haasSupplierSvhcTemplate: IngestionTemplate = {
  id: "haas_supplier_svhc",
  label: "Haas Supplier Conformity",
  description: "Import PDF of a supplier declaration (Haas)",
  anchorScopeId: "supplier",
  resolutions: [
    { id: "anchor", kind: "anchor" },
    {
      id: "target_component",
      kind: "traverse_filter",
      seed: "anchor",
      // "component" is the auto-mirror of haas's "component.supplier"
      // declaration — schema parsing synthesizes the inverse on supplier
      // (name = source scope id).
      path: [
        {
          fromScopeId: "supplier",
          relationPrimitiveName: "component",
          toScopeId: "component",
        },
      ],
      filterPrimitive: "article_number",
      filterValue: COMPONENT_ARTICLE_NR,
      targetScopeId: "component",
      onMissing: "create",
    },
    {
      id: "target_svhc",
      kind: "pk_match",
      scopeId: "substance",
      pkPrimitive: "cas_number",
      value: SVHC_CAS_NUMBER,
      onMissing: "create",
    },
  ],
  writes: [
    {
      id: "set_supplier_name",
      kind: "set_primitive",
      target: "anchor",
      primitive: { scopeId: "supplier", primitiveName: "supplier_name" },
      value: SUPPLIER_NAME,
    },
    {
      id: "set_svhc_concentration",
      kind: "set_primitive",
      target: "target_component",
      primitive: { scopeId: "component", primitiveName: "svhc_concentration" },
      value: SVHC_CONCENTRATION,
    },
    {
      id: "set_substance_name",
      kind: "set_primitive",
      target: "target_svhc",
      primitive: { scopeId: "substance", primitiveName: "substance_name" },
      value: SVHC_SUBSTANCE_NAME,
    },
    {
      id: "link_supplier_component",
      kind: "add_relation",
      fromResolution: "anchor",
      toResolution: "target_component",
      // Driven from the supplier side so the resolution scope (anchor=supplier,
      // target_component=component) lines up with relation.{from,to}ScopeId.
      // Supplier scope has an auto-mirrored "component" relation primitive
      // (synthesised from haas's "component.supplier" declaration); the
      // executor mirrors the link onto component automatically.
      relation: {
        fromScopeId: "supplier",
        relationPrimitiveName: "component",
        toScopeId: "component",
      },
    },
    {
      id: "link_component_svhc",
      kind: "add_relation",
      fromResolution: "target_component",
      toResolution: "target_svhc",
      // haas declares this relation on component as "substance".
      relation: {
        fromScopeId: "component",
        relationPrimitiveName: "substance",
        toScopeId: "substance",
      },
    },
  ],
}
